import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { Pool } from '../../types/pool';
import { ExportPicksButton } from '../../components/ExportPicksButton';

export function AdminDashboard() {
  const { getAccessTokenSilently, user } = useAuth0();
  const [pools, setPools] = useState<Pool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingPool, setEditingPool] = useState<Pool | null>(null);
  const [newDeadline, setNewDeadline] = useState('');
  const [newEntryFee, setNewEntryFee] = useState<string>('');
  const [newMaxEntries, setNewMaxEntries] = useState<string>('');
  const [newStatus, setNewStatus] = useState<string>('');
  const [newSlug, setNewSlug] = useState<string>('');
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Notification control state
  const [notificationAction, setNotificationAction] = useState<string | null>(null);
  const [notificationResult, setNotificationResult] = useState<{ success: boolean; message: string } | null>(null);
  const [selectedPoolForNotification, setSelectedPoolForNotification] = useState<string>('');

  // Test-email form state — recipient defaults to the logged-in admin's address
  // once user loads (prefilled, but editable so admin can send to any address).
  const [testEmailRecipient, setTestEmailRecipient] = useState<string>('');
  const [testEmailTemplate, setTestEmailTemplate] = useState<'GENERIC' | 'DEADLINE_REMINDER' | 'POOL_LOCKED' | 'SCORE_UPDATE'>('GENERIC');
  const [testEmailPoolId, setTestEmailPoolId] = useState<string>('');

  // Completed/cancelled pools clutter the admin list as they accumulate.
  // Hide them by default; small toggle restores them when needed.
  const [showCompleted, setShowCompleted] = useState(false);

  useEffect(() => {
    if (user?.email && !testEmailRecipient) {
      setTestEmailRecipient(user.email);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  useEffect(() => {
    loadPools();
  }, []);

  async function loadPools() {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getPools();
      setPools(data);
    } catch (err) {
      console.error('Failed to load pools:', err);
      setError(err instanceof Error ? err.message : 'Failed to load pools');
    } finally {
      setLoading(false);
    }
  }

  function openPoolEditor(pool: Pool) {
    setEditingPool(pool);
    // Convert deadline to local datetime-local format (YYYY-MM-DDTHH:mm)
    const deadline = new Date(pool.deadline);
    const localDateTime = new Date(deadline.getTime() - deadline.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    setNewDeadline(localDateTime);
    // Set entry fee (convert Decimal to number, handle null)
    setNewEntryFee(pool.entryFee ? String(pool.entryFee) : '0');
    // Set max entries (empty string means unlimited)
    setNewMaxEntries(pool.maxEntries ? String(pool.maxEntries) : '');
    // Set current status
    setNewStatus(pool.status);
    // Set slug
    setNewSlug(pool.slug || '');
    setUpdateError(null);
  }

  function closePoolEditor() {
    setEditingPool(null);
    setNewDeadline('');
    setNewEntryFee('');
    setNewMaxEntries('');
    setNewStatus('');
    setNewSlug('');
    setUpdateError(null);
  }

  async function handleUpdatePool() {
    if (!editingPool || !newDeadline) return;

    try {
      setUpdating(true);
      setUpdateError(null);
      const token = await getAccessTokenSilently();
      
      // Convert local datetime to ISO string
      const deadlineISO = new Date(newDeadline).toISOString();
      
      // Parse entry fee
      const entryFee = parseFloat(newEntryFee) || 0;
      
      // Parse max entries (empty = null = unlimited)
      const maxEntries = newMaxEntries.trim() ? parseInt(newMaxEntries, 10) : null;

      await api.updatePool(editingPool.id, {
        deadline: deadlineISO,
        entryFee: entryFee,
        status: newStatus,
        maxEntries: maxEntries,
        ...(newSlug.trim() ? { slug: newSlug.trim() } : {}),
      }, token);
      
      // Reload pools to show updated values
      await loadPools();
      closePoolEditor();
    } catch (err) {
      console.error('Failed to update pool:', err);
      setUpdateError(err instanceof Error ? err.message : 'Failed to update pool');
    } finally {
      setUpdating(false);
    }
  }

  // Notification control functions
  async function handleSendTestEmail() {
    if (!testEmailRecipient.trim()) {
      setNotificationResult({ success: false, message: 'Please enter a recipient email address.' });
      return;
    }
    if (testEmailTemplate !== 'GENERIC' && !testEmailPoolId) {
      setNotificationResult({ success: false, message: 'This template needs a pool — please select one.' });
      return;
    }
    try {
      setNotificationAction('test');
      setNotificationResult(null);
      const token = await getAccessTokenSilently();
      const result = await api.sendTestTemplateEmail(
        testEmailRecipient.trim(),
        testEmailTemplate,
        testEmailTemplate === 'GENERIC' ? undefined : testEmailPoolId,
        token,
      );
      setNotificationResult({ success: result.success, message: result.message });
    } catch (err) {
      setNotificationResult({ success: false, message: err instanceof Error ? err.message : 'Failed to send test email' });
    } finally {
      setNotificationAction(null);
    }
  }

  async function handleTriggerDeadlineCheck() {
    try {
      setNotificationAction('deadline');
      setNotificationResult(null);
      const token = await getAccessTokenSilently();
      const result = await api.triggerDeadlineCheck(token);
      setNotificationResult({ success: true, message: `Deadline check complete: ${result.sent} sent, ${result.errors} errors` });
    } catch (err) {
      setNotificationResult({ success: false, message: err instanceof Error ? err.message : 'Failed to trigger deadline check' });
    } finally {
      setNotificationAction(null);
    }
  }

  async function handleTriggerScoreNotifications() {
    try {
      setNotificationAction('scores');
      setNotificationResult(null);
      const token = await getAccessTokenSilently();
      const result = await api.triggerScoreNotifications(token);
      setNotificationResult({
        success: true,
        message: `Score notifications: ${result.poolsProcessed} pools, ${result.totalSent} sent, ${result.totalErrors} errors`
      });
    } catch (err) {
      setNotificationResult({ success: false, message: err instanceof Error ? err.message : 'Failed to trigger score notifications' });
    } finally {
      setNotificationAction(null);
    }
  }

  async function handleSendPoolLockedNotifications() {
    if (!selectedPoolForNotification) return;
    try {
      setNotificationAction('locked');
      setNotificationResult(null);
      const token = await getAccessTokenSilently();
      const result = await api.sendPoolLockedNotifications(selectedPoolForNotification, token);
      const poolName = pools.find(p => p.id === selectedPoolForNotification)?.name || 'Pool';
      setNotificationResult({
        success: true,
        message: `${poolName}: ${result.sent} sent, ${result.skipped} skipped, ${result.errors} errors`
      });
    } catch (err) {
      setNotificationResult({ success: false, message: err instanceof Error ? err.message : 'Failed to send pool locked notifications' });
    } finally {
      setNotificationAction(null);
    }
  }

  async function handleSendPoolScoreUpdate() {
    if (!selectedPoolForNotification) return;
    try {
      setNotificationAction('poolScore');
      setNotificationResult(null);
      const token = await getAccessTokenSilently();
      const result = await api.sendPoolScoreUpdate(selectedPoolForNotification, token);
      const poolName = pools.find(p => p.id === selectedPoolForNotification)?.name || 'Pool';
      setNotificationResult({
        success: true,
        message: `${poolName}: ${result.sent} sent, ${result.skipped} skipped, ${result.errors} errors`
      });
    } catch (err) {
      setNotificationResult({ success: false, message: err instanceof Error ? err.message : 'Failed to send score update' });
    } finally {
      setNotificationAction(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-medium">Error loading pools</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-2">Manage pools, import data, and enter results</p>
          </div>
          <Link
            to="/admin/bracket"
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2 shadow-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            NCAA Bracket
          </Link>
          <Link
            to="/admin/world-cup"
            className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2 shadow-md"
          >
            <span className="text-lg leading-none">⚽</span>
            World Cup
          </Link>
          <Link
            to="/admin/broadcast"
            className="px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2 shadow-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
            Broadcast
          </Link>
          <Link
            to="/admin/pools/create"
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2 shadow-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create New Pool
          </Link>
          <Link
            to="/admin/payments"
            className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors flex items-center gap-2 shadow-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Manage Payments
          </Link>
          <Link
            to="/admin/feedback"
            className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors shadow-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            View Feedback
          </Link>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">Total Pools</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">{pools.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">Open Pools</div>
          <div className="text-3xl font-bold text-green-600 mt-2">
            {pools.filter(p => p.status === 'OPEN').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">Active Pools</div>
          <div className="text-3xl font-bold text-blue-600 mt-2">
            {pools.filter(p => p.status === 'IN_PROGRESS').length}
          </div>
        </div>
      </div>

      {/* Notification Controls */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">📧 Notification Controls</h2>
        </div>
        <div className="p-6">
          {/* Result Message */}
          {notificationResult && (
            <div className={`mb-4 p-4 rounded-lg ${notificationResult.success ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
              <div className="flex items-center gap-2">
                {notificationResult.success ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                <span>{notificationResult.message}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Global Actions */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Global Actions</h3>

              {/* Test Email — recipient + template + pool selector */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Test Email</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Recipient</label>
                    <input
                      type="email"
                      value={testEmailRecipient}
                      onChange={(e) => setTestEmailRecipient(e.target.value)}
                      placeholder="recipient@example.com"
                      disabled={notificationAction !== null}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Template</label>
                    <select
                      value={testEmailTemplate}
                      onChange={(e) => setTestEmailTemplate(e.target.value as typeof testEmailTemplate)}
                      disabled={notificationAction !== null}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                    >
                      <option value="GENERIC">Generic Test (no pool data)</option>
                      <option value="DEADLINE_REMINDER">Deadline Reminder</option>
                      <option value="POOL_LOCKED">Pool Locked</option>
                      <option value="SCORE_UPDATE">Score Update</option>
                    </select>
                  </div>
                </div>

                {/* Pool selector — only when template needs pool data */}
                {testEmailTemplate !== 'GENERIC' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Pool (provides name + deadline)</label>
                    <select
                      value={testEmailPoolId}
                      onChange={(e) => setTestEmailPoolId(e.target.value)}
                      disabled={notificationAction !== null}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                    >
                      <option value="">Select a pool…</option>
                      {pools.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.type})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <button
                  onClick={handleSendTestEmail}
                  disabled={notificationAction !== null}
                  className="inline-flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm rounded-lg transition-colors disabled:opacity-50"
                >
                  {notificationAction === 'test' ? (
                    <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  )}
                  Send Test
                </button>
                <p className="text-xs text-gray-500">
                  User-side fields (rank, score, leader name) use clearly-labeled placeholders so the recipient knows it's a test.
                </p>
              </div>

              {/* Other global actions (Check Deadlines, Trigger Score Updates) */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleTriggerDeadlineCheck}
                  disabled={notificationAction !== null}
                  className="inline-flex items-center px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg transition-colors disabled:opacity-50"
                >
                  {notificationAction === 'deadline' ? (
                    <div className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  Check Deadlines
                </button>

                <button
                  onClick={handleTriggerScoreNotifications}
                  disabled={notificationAction !== null}
                  className="inline-flex items-center px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg transition-colors disabled:opacity-50"
                >
                  {notificationAction === 'scores' ? (
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  )}
                  Trigger All Score Updates
                </button>
              </div>
            </div>

            {/* Pool-Specific Actions */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Pool-Specific Actions</h3>
              <div className="flex items-center gap-3">
                <select
                  value={selectedPoolForNotification}
                  onChange={(e) => setSelectedPoolForNotification(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a pool...</option>
                  {pools.map((pool) => (
                    <option key={pool.id} value={pool.id}>
                      {pool.name} ({pool.status})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleSendPoolLockedNotifications}
                  disabled={notificationAction !== null || !selectedPoolForNotification}
                  className="inline-flex items-center px-4 py-2 bg-green-100 hover:bg-green-200 text-green-800 rounded-lg transition-colors disabled:opacity-50"
                >
                  {notificationAction === 'locked' ? (
                    <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  )}
                  Send Pool Locked
                </button>

                <button
                  onClick={handleSendPoolScoreUpdate}
                  disabled={notificationAction !== null || !selectedPoolForNotification}
                  className="inline-flex items-center px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-800 rounded-lg transition-colors disabled:opacity-50"
                >
                  {notificationAction === 'poolScore' ? (
                    <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  )}
                  Send Score Update
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              <strong>Note:</strong> Deadline reminders are sent automatically 24 hours before deadline.
              Score updates are sent automatically after you enter results (for pools set to "After Results" mode).
            </p>
          </div>
        </div>
      </div>

      {/* Pools List */}
      {(() => {
        const completedCount = pools.filter(
          (p) => p.status === 'COMPLETED' || p.status === 'CANCELLED',
        ).length;
        const visiblePools = showCompleted
          ? pools
          : pools.filter((p) => p.status !== 'COMPLETED' && p.status !== 'CANCELLED');
        return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Manage Pools
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({visiblePools.length}
              {completedCount > 0 && !showCompleted ? ` shown · ${completedCount} completed hidden` : ''})
            </span>
          </h2>
          {completedCount > 0 && (
            <button
              type="button"
              onClick={() => setShowCompleted((prev) => !prev)}
              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
            >
              {showCompleted
                ? 'Hide completed'
                : `Show ${completedCount} completed`}
            </button>
          )}
        </div>
        <div className="divide-y divide-gray-200">
          {visiblePools.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              {pools.length === 0
                ? 'No pools found. Create a pool to get started.'
                : 'No active pools. Click "Show completed" above to see past pools.'}
            </div>
          ) : (
            visiblePools.map((pool) => (
              <div key={pool.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{pool.name}</h3>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                      <span className="capitalize">{pool.type.replace('_', ' ')}</span>
                      <span>•</span>
                      <span className={
                        pool.status === 'OPEN' ? 'text-green-600' :
                        pool.status === 'IN_PROGRESS' ? 'text-blue-600' :
                        pool.status === 'COMPLETED' ? 'text-gray-500' :
                        'text-gray-600'
                      }>
                        {pool.status}
                      </span>
                      <span>•</span>
                      <span>{pool._count?.entries || 0} entries</span>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <button
                      onClick={() => openPoolEditor(pool)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 border border-gray-300 rounded-lg transition-colors"
                    >
                      Edit Pool
                    </button>
                    <Link
                      to={`/admin/pools/${pool.id}/import`}
                      className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      Import Data
                    </Link>
                    <Link
                      to={
                        pool.type === 'NCAA_BASKETBALL_KNOCKOUT'
                          ? `/admin/pools/${pool.id}/knockout`
                          : pool.type === 'NCAA_BASKETBALL_PICK6'
                          ? `/admin/pools/${pool.id}/pick6`
                          : `/admin/pools/${pool.id}/results`
                      }
                      className="px-4 py-2 text-sm font-medium text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    >
                      Enter Results
                    </Link>
                    {(pool.type === 'NCAA_BASKETBALL_KNOCKOUT' || pool.type === 'NCAA_BASKETBALL_PICK6') && (
                      <Link
                        to={`/admin/pools/${pool.id}/bracket`}
                        className="px-4 py-2 text-sm font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      >
                        Bracket
                      </Link>
                    )}
                    {(pool.type === 'NHL_PLAYOFF' || pool.type === 'NBA_PLAYOFF' || pool.type === 'NFL_PLAYOFF' || pool.type === 'NFL_NO_REPEAT') && (
                      <Link
                        to={`/admin/pools/${pool.id}/stats`}
                        className="px-4 py-2 text-sm font-medium text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                      >
                        Stats
                      </Link>
                    )}
                    {(pool.type === 'NHL_PLAYOFF' || pool.type === 'NBA_PLAYOFF') && (
                      <Link
                        to={`/admin/pools/${pool.id}/playoff-setup`}
                        className="px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      >
                        Playoff Setup
                      </Link>
                    )}
                    {(pool.type === 'NHL_PLAYOFF' || pool.type === 'NBA_PLAYOFF' || pool.type === 'NFL_PLAYOFF' || pool.type === 'NFL_NO_REPEAT' || pool.type === 'CFP') && (
                      <Link
                        to={`/admin/pools/${pool.id}/players`}
                        className="px-4 py-2 text-sm font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      >
                        Players
                      </Link>
                    )}
                    <Link
                      to={`/admin/pools/${pool.id}/teams`}
                      className="px-4 py-2 text-sm font-medium text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                    >
                      Edit Teams
                    </Link>
                    <ExportPicksButton
                      poolId={pool.id}
                      poolName={pool.name}
                      deadline={pool.deadline}
                      className="inline-block"
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
        );
      })()}

      {/* Pool Editor Modal */}
      {editingPool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Edit Pool Settings</h3>
              <p className="text-sm text-gray-600 mt-1">{editingPool.name}</p>
            </div>
            
            <div className="px-6 py-4">
              {/* Slug */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL Slug
                </label>
                <input
                  type="text"
                  value={newSlug}
                  onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. 2026-nba-playoff-pool"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Used in shareable URLs: propoolpicks.com/pools/<strong>{newSlug || 'your-slug'}</strong>
                </p>
              </div>

              {/* Entry Deadline */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entry Deadline
                </label>
                <input
                  type="datetime-local"
                  value={newDeadline}
                  onChange={(e) => setNewDeadline(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Pool Status */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pool Status
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="OPEN">Open - Accepting Entries</option>
                  <option value="LOCKED">Locked - No More Entries</option>
                  <option value="IN_PROGRESS">In Progress - Games Started</option>
                  <option value="COMPLETED">Completed - Pool Finished</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {newStatus === 'OPEN' && 'Users can join and edit picks'}
                  {newStatus === 'LOCKED' && 'Picks are locked but games haven\'t started'}
                  {newStatus === 'IN_PROGRESS' && 'Games in progress, picks visible to all'}
                  {newStatus === 'COMPLETED' && 'All games finished, final results shown'}
                  {newStatus === 'CANCELLED' && 'Pool cancelled, entries refunded'}
                </p>
              </div>

              {/* Entry Fee */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entry Fee ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newEntryFee}
                  onChange={(e) => setNewEntryFee(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                />
              </div>

              {/* Max Entries Per User */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Entries Per User
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={newMaxEntries}
                  onChange={(e) => setNewMaxEntries(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Unlimited"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Leave empty for unlimited entries. Set a number to cap entries per user.
                </p>
              </div>

              {updateError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                  {updateError}
                </div>
              )}

              <div className="text-xs text-gray-500">
                ⚠️ Changes will affect all future entries. Existing entries are not affected.
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={closePoolEditor}
                disabled={updating}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdatePool}
                disabled={updating || !newDeadline}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {updating && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                {updating ? 'Updating...' : 'Update Pool'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
